import React from 'react'
import { requestIDOf } from './request-id'

// A notification (toast, modal, alert) whose text is an API error's message
// shows the error's reference with it (error-reference.js).

const NOTICE = Symbol.for('edxr.error-notice')
const SHOWN = Symbol.for('edxr.error-shown')

// A toast's or modal's text with the error behind it: the item shows the
// message and the error's reference. Without a reference (a network failure,
// a client-side error) it is the message itself, exactly as before.
export const withReference = (message, error) => (requestIDOf(error) ? { [NOTICE]: true, message, error } : message)

// How long an item with a reference stays at least, so it can be copied.
export const REFERENCE_NOTICE_MS = 20000

const text = v => (typeof v === 'string' && v ? v : null)

// The message an API error carries, from any shape an API layer hands on,
// else axios's own sentence, else fallback.
export const errorMessageOf = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error || typeof error !== 'object') return text(error) || fallback
  const body = (error.response && error.response.data) || error.data
  const inner = error.error
  return (
    (body && typeof body === 'object' && text(body.message)) ||
    (inner && typeof inner === 'object' && text(inner.message)) ||
    text(inner) ||
    text(error.message) ||
    fallback
  )
}

// What a notification item shows: its message, and the error behind it. The
// text is a string (or element), withReference's pair, or an API error
// itself (the old useApiFormState hands axios's error to modal.error).
export const noticeParts = value => {
  if (value && value[NOTICE]) return { message: value.message, error: value.error }
  if (value && typeof value === 'object' && !React.isValidElement(value)) return { message: errorMessageOf(value), error: value }
  return { message: value, error: null }
}

// How long an item stays: a reference keeps it up long enough to copy.
export const noticeClose = (value, close) =>
  typeof close === 'number' && requestIDOf(noticeParts(value).error) ? Math.max(close, REFERENCE_NOTICE_MS) : close

// An error a notification has shown (the old useApiFormState's modal, a
// page's toast) is marked, so a surface it is rethrown to -- a Form's or
// FormNew's failed-save alert (save-failure.js) -- doesn't show it again.
// Non-enumerable, like the reference: the error is otherwise unchanged.
export const markShown = error => {
  if (error && typeof error === 'object' && !Object.isFrozen(error)) {
    try {
      Object.defineProperty(error, SHOWN, { value: true, enumerable: false, configurable: true })
    } catch (e) {
      // an object that can't take a property may be shown twice; never lost
    }
  }
  return error
}

export const wasShown = error => !!(error && typeof error === 'object' && error[SHOWN])
